import * as React from 'react';
import Dump from "../components/dump";
import Spinner, { SpinnerColorsEnum } from "../components/spinner";
import { applyDiscount, fetchDiscount, fetchDiscountCodes, fetchPayable, fetchSale, gotoMessagePaymentFailure, gotoMessagePaymentSuccess, reinitPayment, reviewDetails } from "../loaders";
import ColumnMain from "../src/columnMain";
import ColumnSide from "../src/columnSide";
import Layout from "../src/layout";
import SummaryExtended from "../src/summaryExtended";
import { useEffect, useState, useContext } from 'react';
import { ContextSession } from "./app";
import { WidgetIframe } from '@resamania/payment-react';
import { formatPriceWithCurrency, isSubscription, parseStartDate } from "./utils";
import { resolvePlanName } from "./planNameOverrides";
import { DiscountBanner, type Discount } from "../components/discount";

// Discount codes are built but the club doesn't use them yet, so the input is hidden to
// let the automatic goodwill-gesture discount ship to production on its own. Flip this
// back on (and have Resamania re-enable the code endpoints for our client) when the club
// adopts codes.
const SHOW_DISCOUNT_CODE = false;

type Props = Readonly<{
	setState: Function,
	offer?: {},
}>

function Payment({ setState, offer, ...otherProps }: Props) {
	let yesterday = new Date();
	yesterday.setHours(0, 0, 0);

	const state = useContext(ContextSession);
	const [sale, setSale] = useState(null);
	const [payable, setPayable] = useState(null);
	const [reinitialised, setReinitialised] = useState(false);

	// The automatic goodwill-gesture discount applied to this sale (if the club is
	// running one). Auto-applied server-side when the sale was created; here we just
	// fetch its text to tell the customer about it.
	const [discount, setDiscount] = useState<Discount | null>(null);

	// SPIKE — discount codes.
	const [discountCode, setDiscountCode] = useState('');
	const [discountMsg, setDiscountMsg] = useState<{ ok: boolean, text: string } | null>(null);
	const [applyingDiscount, setApplyingDiscount] = useState(false);

	// Re-read the payable/sale into local state (summary amount comes from here).
	const loadDetails = () => {
		fetchPayable(state.hash).then((data) => setPayable(data));
		fetchSale(state.hash).then((data) => setSale(data));
	};

	useEffect(() => {
		// Re-issue the payable so the widget mounts against a fresh token, THEN load
		// the payable/sale details (which now reflect the new payable). If reinit
		// reports the payment already went through, setState moves us off this screen.
		reinitPayment(state.hash, setState)
			.catch(() => { /* fall through and render with what we have */ })
			.finally(() => {
				setReinitialised(true);
				loadDetails();
			});

		// The automatic discount is already on the sale (applied at sale creation); fetch
		// its text so we can tell the customer they're getting it.
		fetchDiscount(state.hash).then((data) => setDiscount(data ?? null));

		// SPIKE: discover any discount codes the club has made available for this sale.
		// Hidden path (SHOW_DISCOUNT_CODE) — skip the lookup so we don't hit the code
		// endpoint, which isn't enabled for our client in production.
		if (SHOW_DISCOUNT_CODE) {
			fetchDiscountCodes(state.hash).then((codes) => {
				if (Array.isArray(codes) && codes.length) {
					console.log('available discount codes', codes);
				}
			});
		}
	}, []);

	// SPIKE: apply a typed code, then refresh the payable/sale so the widget and summary
	// reflect the discounted total.
	const handleApplyDiscount = () => {
		const code = discountCode.trim();
		if (!code) return;

		setApplyingDiscount(true);
		setDiscountMsg(null);

		applyDiscount(state.hash, code, setState)
			.then(() => {
				loadDetails();
				setDiscountMsg({ ok: true, text: 'Discount applied.' });
			})
			.catch(() => setDiscountMsg({ ok: false, text: 'That code could not be applied.' }))
			.finally(() => setApplyingDiscount(false));
	};

	// A one-off product is paid in full, once. "First month" and "joining fee"
	// describe a renewing membership and are wrong for it.
	const subscription = isSubscription(offer);

	const handleSuccess = (payload, { provider }) => {
		console.log('success');
		gotoMessagePaymentSuccess(state.hash, setState);
	}

	const handleFailure = (payload, { provider }) => {
		console.log('failure');
		gotoMessagePaymentFailure(state.hash, setState);
	}

	return (<>
		<Layout name={"PAYMENT"} innerClassName="items-stretch">
			<ColumnMain banner={discount ? <DiscountBanner discount={discount}/> : null}>
				{(!reinitialised || sale === null  || payable === null )? <div className="max-w-screen-xl mx-auto text-center px-4 sm:px-8 my-32"><Spinner color={SpinnerColorsEnum.Black}/></div> :
				<div>
					<h3>{subscription ? 'YOUR FIRST MONTH’S MEMBERSHIP' : 'YOUR PAYMENT'}</h3>
					<p className="font-bold">
						{subscription ? 'Pay for your first month plus joining fee' : <>Pay for your {resolvePlanName(offer?.showcaseIri, offer?.name)}</>}
					</p>

					<p className="font-bold">{formatPriceWithCurrency((payable?.amount / 100), payable?.currency)}</p>

					{/*<button className="text-xl w-full">*/}
					{/*	<img src="/images/lock.svg" alt="" width={16} height={21} className="mr-3 mb-1 inline-block"/>*/}
					{/*	PAY £49.95*/}
					{/*</button>*/}

					<p className="mb-2 font-bold">
						<img src="/images/ssl.png" alt="" width={18} height={22} className="mr-3 mb-1 inline-block"/>
						Secure Payments - SSL Encrypted
					</p>
					<p className="mb-0 text-sm">
						Your payment details are protected with industry-standard encryption for a safe and secure checkout experience.
					</p>

					{/* Discount code entry. Applies to the sale and re-issues the payable, so
					    the widget below and the summary update to the reduced total. Hidden
					    until the club adopts codes — see SHOW_DISCOUNT_CODE. */}
					{SHOW_DISCOUNT_CODE &&
					<div className="my-6 rounded-2xl border border-[#e5e5e5] p-4 sm:p-5">
						<label htmlFor="discountCode" className="block text-[11px] font-bold uppercase tracking-wider text-[#737373] mb-2">Have a discount code?</label>
						<div className="flex items-stretch gap-2">
							<input
								id="discountCode"
								type="text"
								placeholder="Enter code"
								className="grow !mb-0"
								value={discountCode}
								onChange={(e) => setDiscountCode(e.target.value)}
							/>
							<button type="button" className="!mb-0 shrink-0 px-6 disabled:opacity-40 disabled:cursor-not-allowed" disabled={applyingDiscount || !discountCode.trim()} onClick={handleApplyDiscount}>
								{applyingDiscount ? 'Applying…' : 'Apply'}
							</button>
						</div>
						{discountMsg &&
							<p className={"text-xs mt-1 mb-0 " + (discountMsg.ok ? "text-green-700" : "text-red-500")}>{discountMsg.text}</p>
						}
					</div>}

					<WidgetIframe
						src={state?.widgetUrl}
						style={{width: '100%', height: 500, border: 'none'}}
						onActionSuccess={handleSuccess}
						onActionFailure={handleFailure}
					/>

					<p className="text-sm mt-4 mb-0">
						Need to check your details?{' '}
						<button
							type="button"
							className="!border-0 !bg-transparent !p-0 !m-0 !min-h-0 underline !text-inherit !normal-case !font-normal cursor-pointer"
							onClick={() => reviewDetails(state.hash, setState)}
						>
							Review your details
						</button>
					</p>
				</div>}
			</ColumnMain>
			<ColumnSide innerClassName="h-full !bg-grey-100">
				<SummaryExtended
					offer={offer}
					startDate={parseStartDate(state?.startDate) ?? new Date()}
					amountDueToday={payable?.amount != null ? payable.amount / 100 : undefined}
					discount={discount}
				/>
			</ColumnSide>
		</Layout>

		{/*<Dump value={state?.widgetUrl}/>*/}
		{/*<Dump value={payable}/>*/}
		{/*<Dump value={sale} label="sale"/>*/}
	</>)
}

export default Payment;
